import { useState, useCallback, useMemo } from "react";
import { getAiFiltersByName, getAiFiltersByNamePublic, type AIFilterParamsType, type AiFilterResponseType } from "../services";
import { mapAiImages } from "../components/AiFilters/aiFilter.helpers";
import type { AiFiltersFormType } from "utils";
import type { AiFilterItemType } from "../components/AiFilters/types";

interface UseAiFiltersDataOptions {
  reservationId?: string;
  initialNameFilters?: string;
  publicAccess?: boolean;  // <-- ÚJ
}

interface UseAiFiltersDataReturn {
  baseImagesList: AiFilterResponseType[];
  formattedImagesList: AiFilterItemType[];
  isLoading: boolean;
  error: string | null;
  fetchFilters: (nameFilters?: string) => Promise<void>;
  filterValue: AiFiltersFormType | null;
  setFilterValue: React.Dispatch<React.SetStateAction<AiFiltersFormType | null>>;
}

export function useAiFiltersData(options: UseAiFiltersDataOptions = {}): UseAiFiltersDataReturn {
  const { reservationId, initialNameFilters, publicAccess } = options;  // <-- ÚJ
  
  const [baseImagesList, setBaseImagesList] = useState<AiFilterResponseType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterValue, setFilterValue] = useState<AiFiltersFormType | null>({
    aspectRatio: "16x9",
    gender: "man",
    nameFilters: initialNameFilters || "",
    groupFilters: false,
    randomFilter: false,
  });

  const fetchFilters = useCallback(async (nameFilters?: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const nameFiltersArray = nameFilters && nameFilters !== "" 
        ? nameFilters.split(",") 
        : [];
      
      const filter: AIFilterParamsType = {
        nameFilters: nameFiltersArray,
        reservationId,
      };
      
      // ====== VÁLTOZTATÁS: publicAccess flag alapján választ ======
      const resp = publicAccess
        ? await getAiFiltersByNamePublic(filter)
        : await getAiFiltersByName(filter);
      
      // Filter by groupFilterCount === 1 && (type === 0 || type === null)
      const filtered = resp.filter(
        (f) => f.groupFilterCount === 1 && (f.type === 0 || f.type === null)
      );
      
      setBaseImagesList(filtered);
    } catch (err) {
      console.error("Failed to fetch AI filters:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch filters");
    } finally {
      setIsLoading(false);
    }
  }, [reservationId, publicAccess]);  // <-- publicAccess hozzáadva a dependency-khez

  const formattedImagesList = useMemo(() => {
    if (!filterValue) return [];
    // "Video only" narrows the already-fetched list client-side, replacing the old
    // workaround of typing "+" into the name filter.
    const source = filterValue.videoOnly
      ? baseImagesList.filter((f) => f.hasVideo)
      : baseImagesList;
    return mapAiImages(source, filterValue);
  }, [baseImagesList, filterValue]);

  return {
    baseImagesList,
    formattedImagesList,
    isLoading,
    error,
    fetchFilters,
    filterValue,
    setFilterValue,
  };
}